// pages/SkillsPage.jsx
import React, { useState } from 'react';
import { Award, Shield, TrendingUp, Search, Filter, Info, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/common/BottomNav';
import SkillVerification from '../components/trust/SkillVerification';
import SkillBadgeList from '../components/trust/SkillBadgeList';
import SkillBadge from '../components/trust/SkillBadge'; // Added missing import
import { useSkills } from '../context/SkillContext';
import { useAuth } from '../context/AuthContext';
import { skillCategories } from '../utils/skillVerification';

const SkillsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    skills, 
    userSkills, 
    getSkillsByCategory, 
    getSkillVerificationStatus,
    meetsSkillRequirements 
  } = useSkills();
  
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  // Filter skills
  const filteredSkills = skills.filter(skill => {
    const matchesCategory = selectedCategory === 'All' || skill.category === selectedCategory;
    const matchesSearch = skill.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         skill.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group skills by category
  const groupedSkills = filteredSkills.reduce((acc, skill) => {
    if (!acc[skill.category]) acc[skill.category] = [];
    acc[skill.category].push(skill);
    return acc;
  }, {});

  const handleVerifySkill = (skill) => {
    setSelectedSkill(skill);
    setShowVerificationModal(true);
  };

  const handleGoBack = () => {
    navigate('/profile');
  };

  return (
    <div className="container mx-auto px-4 pb-20">
      <div className="mt-6 mb-6">
        <div className="flex items-center mb-2">
          <button 
            className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow mr-3 hover:bg-gray-50"
            onClick={handleGoBack}
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold">Skills & Certifications</h1>
        </div>
        <p className="text-gray-600 mt-2 ml-11">Verify your skills to unlock premium job opportunities</p>
      </div>

      {/* User Skills Summary */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Your Skills</h2>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Shield className="w-4 h-4" />
            <span>Trust Score: {user?.trust?.score?.toFixed(1) || '3.0'}</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-900">Verified Skills</span>
            </div>
            <div className="text-2xl font-bold text-green-600">
              {userSkills.verified.length}
            </div>
            <p className="text-sm text-green-700 mt-1">
              Ready for jobs
            </p>
          </div>
          
          <div className="bg-yellow-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-5 h-5 text-yellow-600" />
              <span className="font-medium text-yellow-900">Pending Review</span>
            </div>
            <div className="text-2xl font-bold text-yellow-600">
              {userSkills.pending.length}
            </div>
            <p className="text-sm text-yellow-700 mt-1">
              Under verification
            </p>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">Potential Earnings</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {skills.reduce((sum, skill) => {
                const status = getSkillVerificationStatus(skill.id);
                return status === 'unverified' && meetsSkillRequirements(skill.id) 
                  ? sum + skill.tokenReward 
                  : sum;
              }, 0)}
            </div>
            <p className="text-sm text-blue-700 mt-1">
              Tokens available
            </p>
          </div>
        </div>
        
        {/* Current Skills */}
        {(userSkills.verified.length > 0 || userSkills.pending.length > 0) && (
          <div className="mt-4 pt-4 border-t">
            <SkillBadgeList
              skills={[...userSkills.verified, ...userSkills.pending]}
              maxDisplay={5}
              showAddButton={false}
            />
          </div>
        )}
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search skills..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border rounded-lg px-3 py-2"
            >
              <option value="All">All Categories</option>
              {skillCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Skills by Category */}
      <div className="space-y-6">
        {Object.entries(groupedSkills).map(([category, categorySkills]) => (
          <div key={category} className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-lg">{category}</h3>
              <p className="text-sm text-gray-600">
                {categorySkills.length} skills available
              </p>
            </div>
            
            <div className="divide-y">
              {categorySkills.map(skill => {
                const status = getSkillVerificationStatus(skill.id);
                const canVerify = meetsSkillRequirements(skill.id);
                
                return (
                  <div key={skill.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h4 className="font-medium">{skill.name}</h4>
                          <SkillBadge
                            skill={skill}
                            status={status}
                            size="tiny"
                          />
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {skill.description}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-gray-500">
                            Tier {skill.tier}
                          </span>
                          <span className="text-yellow-600 font-medium">
                            +{skill.tokenReward} tokens
                          </span>
                          <span className="text-gray-500">
                            Requires {skill.trustRequirement}+ trust
                          </span>
                        </div>
                      </div>
                      
                      <div className="ml-4">
                        {status === 'verified' ? (
                          <div className="text-green-600 text-sm font-medium">
                            ✓ Verified
                          </div>
                        ) : status === 'pending' ? (
                          <div className="text-yellow-600 text-sm font-medium">
                            Under Review
                          </div>
                        ) : canVerify ? (
                          <button
                            onClick={() => handleVerifySkill(skill)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700"
                          >
                            Verify
                          </button>
                        ) : (
                          <div className="text-gray-500 text-sm">
                            <div className="flex items-center gap-1">
                              <Info className="w-4 h-4" />
                              <span>Need {skill.trustRequirement}+ trust</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Verification Modal */}
      {showVerificationModal && selectedSkill && (
        <SkillVerification
          skillId={selectedSkill.id}
          onClose={() => {
            setShowVerificationModal(false);
            setSelectedSkill(null);
          }}
          onComplete={() => {
            // Refresh skills or show success
          }}
        />
      )}

      <BottomNav />
    </div>
  );
};

export default SkillsPage;